import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { SectionTitle, ClaimNote } from "@/components/Wave";
import { ComingSoon } from "@/components/ComingSoon";
import { PRODUCT_COPY, SCIENCE, TIPS } from "@/data/content";
import { subPrice, subPct, productImage } from "@/lib/api";
import { Usd, usePrice, UsdOnlyNote } from "@/components/Usd";
import { AddToCart } from "@/components/ProductPanel";
import { ModeToggle, hasSub } from "@/components/ProductExtras";

export const Science = () => (
  <section data-testid="science-section" className="container-gf section">
    <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] items-start">
      <SectionTitle chip="The science bit" title="Why EPA & DHA?"
        sub="EPA and DHA are omega-3 fatty acids found in fish. Grandpa's omega-3 fish oil from sardines and anchovies has EPA 180 mg and DHA 120 mg per serving (serving size: see label)." />
      <div>
        <div className="grid gap-4 sm:grid-cols-3">
          {SCIENCE.map((s) => (
            <div key={s.title} data-testid={`science-${s.title.toLowerCase()}`} className="sticker bg-white p-6">
              <p className="label-chip">{s.title}</p>
              <p className="mt-4 font-display text-xl leading-snug">{s.line}</p>
            </div>
          ))}
        </div>
        <ClaimNote testId="science-claim-disclaimer" />
      </div>
    </div>
  </section>
);

const RangeBuy = ({ p }) => {
  const [mode, setMode] = useState("sub");
  const price = usePrice();
  const sub = hasSub(p) && mode === "sub";
  return (
    <>
      <p data-testid={`product-card-price-${p.id}`} className="mt-5 font-semibold">{sub ? <>{price(subPrice(p))} with Subscribe & save · save {subPct(p)}%</> : <>{price(p.price_usd)} one-time</>}</p>
      {hasSub(p) && <ModeToggle p={p} mode={mode} setMode={setMode} />}
      {p.active && p.available > 0 && (sub
        ? <Link to={`/subscribe/${p.id}`} data-testid={`home-subscribe-${p.id}`} className="mt-4 btn-pop bg-sunny text-navy">Subscribe · {price(subPrice(p))}</Link>
        : <AddToCart p={p} />)}
    </>
  );
};

const RangeCard = ({ p }) => {
  const c = PRODUCT_COPY[p.id];
  return (
    <div data-testid={`product-card-${p.id}`} className="group sticker bg-white p-6 md:p-8 grid grid-cols-[minmax(0,1fr)_auto] gap-6 items-start transition-transform hover:-translate-y-1">
      <div>
        <span className="label-chip">{p.count} {p.unit}</span>
        <h3 className="mt-4 text-2xl md:text-3xl leading-tight"><Link to={`/shop/${p.id}`} data-testid={`product-card-title-${p.id}`}>{p.short_name}</Link></h3>
        <ul className="mt-4 space-y-1 text-sm text-slate-700">{(c?.claims || []).map((cl) => <li key={cl}>{cl}</li>)}</ul>
        <RangeBuy p={p} />
        <Link to={`/shop/${p.id}`} data-testid={`product-card-view-${p.id}`} className="mt-5 inline-flex items-center gap-2 font-semibold underline decoration-sunny decoration-2 underline-offset-4">View product <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
      </div>
      <Link to={`/shop/${p.id}`} tabIndex={-1} aria-hidden="true" className="shrink-0"><img src={productImage(p.id, 400)} alt="" loading="lazy" decoding="async" width="144" height="144" className="w-24 sm:w-36 rounded-2xl object-cover" /></Link>
    </div>
  );
};

export const Range = () => {
  const { products } = useStore();
  return (
    <section data-testid="products-section" className="bg-white/50">
      <div className="container-gf section">
        <SectionTitle chip="The range" title="Two bottles. One easy routine." sub="Omega-3 Fish Oil and a Daily Multivitamin. One-time or Subscribe & save." />
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {products.filter((p) => PRODUCT_COPY[p.id]).map((p) => <RangeCard key={p.id} p={p} />)}
        </div>
        <ClaimNote testId="products-claim-disclaimer" />
        <UsdOnlyNote className="mt-3" testId="range-usd-note" />
        <ComingSoon compact />
      </div>
    </section>
  );
};

export const TipsTeaser = () => (
  <section className="bg-foam">
    <div className="container-gf section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <SectionTitle chip="Grandpa's tips" title="Bite-size wisdom" />
        <Link to="/blog" data-testid="tips-see-all-link" className="btn-pop bg-white self-start">All tips <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {TIPS.slice(0, 3).map((t) => (
          <div key={t.title} className="sticker bg-sand p-6">
            <span className="label-chip bg-white">{t.tag}</span>
            <h3 className="mt-4 text-xl">{t.title}</h3>
            <p className="mt-2 text-slate-700">{t.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Reviews = () => (
  <section data-testid="reviews-section" className="container-gf section">
    <SectionTitle chip="Reviews" title="The legends speak" />
    <div data-testid="reviews-empty-state" className="mt-10 sticker bg-white p-8 md:p-12">
      <p className="font-display text-2xl">No reviews yet.</p>
      <p className="mt-3 text-slate-600 max-w-md">We're brand new and will only ever post real reviews from real customers.</p>
    </div>
  </section>
);
